import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { MapPin, Phone, Globe, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { submitContactForm } from '../mock/mockData';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await submitContactForm(formData);
      if (result.success) {
        toast.success('Thank you for reaching out! We will contact you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
        });
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Address',
      details: ['17 Broadway, Suite B', 'Kissimmee, FL 34741'],
      color: 'text-purple-700',
      bgColor: 'bg-purple-50',
    },
    {
      icon: Phone,
      title: 'Phone',
      details: ['407-705-7281'],
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    },
    {
      icon: Globe,
      title: 'Website',
      details: ['www.VoteWandaRoman.com'],
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
  ];

  return (
    <section id="contact" className="relative py-20 md:py-32 overflow-hidden">
      {/* Premium Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-100/60 via-white to-purple-50"></div>
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: `radial-gradient(circle at 10% 20%, rgba(124, 58, 237, 0.1) 0%, transparent 50%),
                         radial-gradient(circle at 90% 80%, rgba(167, 139, 250, 0.12) 0%, transparent 50%),
                         linear-gradient(135deg, rgba(196, 181, 253, 0.08) 0%, transparent 70%)`
      }}></div>
      
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Contact Us
          </h2>
          <div className="w-24 h-1.5 bg-purple-700 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600">
            Have questions or want to get involved? We'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-8">
                Get in Touch
              </h3>
              <div className="space-y-6">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <Card key={index} className="border-0 shadow-lg">
                      <CardContent className="p-6 flex items-start space-x-4">
                        <div className={`${info.bgColor} w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`${info.color} w-6 h-6`} strokeWidth={2.5} />
                        </div>
                        <div>
                          <div className="font-bold text-gray-900 mb-1">
                            {info.title}
                          </div>
                          {info.details.map((detail, idx) => (
                            <div key={idx} className="text-gray-600">
                              {detail}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-700 to-purple-800 rounded-2xl p-8 text-white shadow-2xl border border-purple-600/50">
              <h4 className="text-2xl font-bold mb-4">Campaign Headquarters</h4>
              <p className="text-purple-100 leading-relaxed">
                Visit us to learn more about our campaign, pick up yard signs, or volunteer your time. We welcome everyone who shares our vision for a better Kissimmee.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="border-0 shadow-2xl bg-white/90 backdrop-blur-sm">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-gray-900 font-semibold mb-2 block">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-gray-900 font-semibold mb-2 block">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-gray-900 font-semibold mb-2 block">
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full"
                    placeholder="(407) 555-0123"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-gray-900 font-semibold mb-2 block">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full min-h-[150px]"
                    placeholder="Tell us how you'd like to get involved or ask us a question..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-purple-700 hover:bg-purple-800 text-white font-bold text-lg py-6 transition-all duration-300 hover:scale-105"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;
